import { useReactFlow, getConnectedEdges, reconnectEdge, getOutgoers } from "reactflow";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { setCutNode } from "redux/actions/flowDesigner/flowsActionCreator";
import { useSelector } from "react-redux";
import { useContext } from "react";
import { TabContextApi } from "layouts/Flow Designer/context";
import { COMMON_COMPONENT_TYPES, EDGE_TYPES } from "constants/flowDesigner";
import { generateEdge } from "layouts/Flow Designer/helpers/common";
import { addErrorLog } from "Logger/logger";
import { addStepToContext } from "redux/actions/flowDesigner/editorContextActionCreator";
import { NODE_TYPES } from "constants/flowDesigner";
import { addContextVariable } from "redux/actions/flowDesigner/editorContextActionCreator";
import { createResourceDependencies } from "redux/actions/portal/resourceDependenciesActionCreator";
import { extractDependenciesFromFlowConfig } from "helpers/resourceDependencies";
import useCreateSubFlowNode from "hooks/useCreateSubFlowNode";
import useHandleStraightEdge from "hooks/useHandleStraightEdge";
import { useHandleFloatingEdge } from "hooks/useHandleFloatingEdge";

export function useCutPaste() {
  const reactFlowInstance = useReactFlow();
  const { getNodes, getNode, setEdges, setNodes, getEdges } = reactFlowInstance;
  const { cutNode } = useSelector((state) => state.designer.flows);
  const dispatch = useDispatch();
  const { autoSaveFlow } = useContext(TabContextApi);
  const { currentSavedFlow, flows } = useSelector((store) => store?.designer?.flows);
  const { createSubFlowNode } = useCreateSubFlowNode();
  const { createStraightEdge } = useHandleStraightEdge();
  const { createFloatingEdge } = useHandleFloatingEdge();

  const recreateNodeDependencies = (nodes) => {
    if (!currentSavedFlow?.id || !nodes.length) return;

    const dependencies = extractDependenciesFromFlowConfig(
      currentSavedFlow.id,
      { nodes, triggers: [] }
    );

    if (dependencies.length) {
      dispatch(
        createResourceDependencies({
          tenantId: currentSavedFlow.tenant_id,
          dependencies
        })
      );
    }
  };

  const addGotoFloatingEdges = (edges, nodes) => {
    const updatedEdges = [...edges];

    nodes.forEach((node) => {
      if (node.data?.type !== COMMON_COMPONENT_TYPES.GOTO) return;

      const targetNodeId = node.data.flowElement?.data?.nodeId?.value;
      if (!targetNodeId) return;

      const floatingEdge = createFloatingEdge(node.id, targetNodeId);
      if (!updatedEdges.some((edge) => edge.id === floatingEdge.id)) {
        updatedEdges.push(floatingEdge);
      }
    });

    return updatedEdges;
  };

  const cut = (id) => {
    const selectedNode = getNodes().find((node) => node.id === id);

    if (selectedNode) {
      toast.success("Node cut successfully");
    }

    if (
      selectedNode?.data?.type === COMMON_COMPONENT_TYPES.CONDITIONAL ||
      selectedNode?.data?.type === COMMON_COMPONENT_TYPES.SELECTION
    ) {
      return dispatch(
        setCutNode({
          node: selectedNode,
          originalNodes: getNodes(),
          originalEdges: getEdges()
        })
      );
    }

    dispatch(
      setCutNode({
        node: selectedNode
      })
    );
  };

  const getNodesAndBranches = (startNodeId, mergeId, nodes, edges) => {
    try {
      const nodesAndBranches = [];

      const traverseNodes = (nodeId) => {
        const node = nodes.find((node) => node.id === nodeId);
        if (!node) return;

        if (!nodesAndBranches.includes(node)) {
          nodesAndBranches.push(node);
        }

        const outgoing = getOutgoers(
          node,
          nodes,
          edges.filter((edge) => edge.type !== "floatingedge")
        );

        if (outgoing.length === 1 && outgoing[0].id === mergeId) {
          if (!nodesAndBranches.includes(outgoing[0])) {
            nodesAndBranches.push(outgoing[0]);
          }
          return;
        }

        outgoing.forEach((outgoer) => {
          traverseNodes(outgoer.id);
        });
      };

      traverseNodes(startNodeId);

      return nodesAndBranches;
    } catch (error) {
      console.log("Error in getNodesAndBranches", error.message);
    }
  };

  const addNewNodesContext = (nodes) => {
    if (nodes.length === 0) return;

    nodes.forEach((node) => {
      if (node.type !== NODE_TYPES.HIDDEN && node.type !== NODE_TYPES.LABEL) {
        dispatch(
          addStepToContext(
            node.data?.componentKey,
            {
              nodeId: node.id,
              type: node.type === "custom" ? "connector" : "component",
              inputData: {},
              outputData: {}
            },
            currentSavedFlow?.name
          )
        );

        if (node.data?.type === COMMON_COMPONENT_TYPES.VARIABLES) {
          dispatch(addContextVariable(node.data?.componentKey, currentSavedFlow?.name));
        }
      }
    });
  };

  // add pasted node to context
  const paste = (source, target) => {
    const nodeToPaste = cutNode.node;
    const { originalNodes, originalEdges } = cutNode;
    const edges = getEdges();
    const controlFlowEdges = edges.filter(
      (edge) => edge.type !== EDGE_TYPES.FLOATING && edge.type !== EDGE_TYPES.STRAIGHT
    );
    const sourceNode = getNode(source);
    const newSourceEdge = getConnectedEdges([getNode(source)], controlFlowEdges)?.find(
      (edge) => edge.source === source
    );

    try {
      if (
        nodeToPaste?.data?.mergeId &&
        (nodeToPaste?.data?.type === COMMON_COMPONENT_TYPES.CONDITIONAL ||
          nodeToPaste?.data?.type === COMMON_COMPONENT_TYPES.SELECTION)
      ) {
        const mergeNode = originalNodes.find((node) => node.id === nodeToPaste.data.mergeId);
        const nodesAndBranches = getNodesAndBranches(
          nodeToPaste.id,
          nodeToPaste.data?.mergeId,
          originalNodes,
          originalEdges
        );

        const nodesAndBranchesIds = nodesAndBranches.map((node) => node.id);
        const nodesAndBranchesEdges = originalEdges.filter((edge) => {
          return (
            nodesAndBranchesIds.includes(edge.source) && nodesAndBranchesIds.includes(edge.target)
          );
        });

        const combinedEdges = edges.concat(nodesAndBranchesEdges);
        const mergeNodeEdge = getConnectedEdges([mergeNode], originalEdges)?.find(
          (edge) => edge.source === mergeNode.id
        );

        let updatedEdges = reconnectEdge(
          newSourceEdge,
          {
            source,
            target: nodeToPaste.id
          },
          combinedEdges
        );

        updatedEdges.push(mergeNodeEdge);

        updatedEdges = reconnectEdge(
          mergeNodeEdge,
          {
            source: mergeNode.id,
            target
          },
          updatedEdges
        );
        updatedEdges = addGotoFloatingEdges(updatedEdges, nodesAndBranches);

        addNewNodesContext(nodesAndBranches);
        recreateNodeDependencies(nodesAndBranches);

        setEdges(updatedEdges);

        setNodes((nodes) =>
          nodes.concat(nodesAndBranches).map((node) => {
            if (node.id === nodeToPaste.id) {
              node.data.isBranch = sourceNode?.data?.isBranch;
            }

            return node;
          })
        );
      } else {
        let updatedEdges = reconnectEdge(
          newSourceEdge,
          {
            source,
            target: nodeToPaste.id
          },
          edges
        );
        updatedEdges = updatedEdges.concat(generateEdge(nodeToPaste.id, target));
        let updatedNodes = getNodes()
          .concat(nodeToPaste)
          .map((node) => {
            if (node.id === nodeToPaste.id) {
              node.data.isBranch = sourceNode?.data?.isBranch;
            }
            return node;
          });

        if (nodeToPaste?.data?.type === COMMON_COMPONENT_TYPES.INVOKE) {
          const subFlowId = nodeToPaste.data.flowElement?.data?.flowId?.value;
          const subFlowData = flows?.find((flow) => flow.id === subFlowId);
          const subFlowNode = createSubFlowNode(nodeToPaste, subFlowData);
          const runSynchronously = nodeToPaste.data.flowElement?.data?.runSynchronously?.value ?? true;

          updatedNodes.push(subFlowNode);
          updatedEdges.push(createStraightEdge(nodeToPaste.id, subFlowNode.id, runSynchronously));
        }

        updatedEdges = addGotoFloatingEdges(updatedEdges, [nodeToPaste]);

        addNewNodesContext([nodeToPaste]);
        recreateNodeDependencies([nodeToPaste]);

        setEdges(updatedEdges);

        setNodes(updatedNodes);
      }

      dispatch(setCutNode(null));
      autoSaveFlow(reactFlowInstance, null, null, true);
    } catch (error) {
      console.log(error);
      addErrorLog("Flow designer paste node", error.message);
    }
  };

  return { cut, paste };
}
